// Mirrors the backend contract in backend/src/modules/leave/leave.controller.ts
// (Concedii). Kept free of DOM and of web-only packages so the Expo app imports
// it verbatim as `@web/lib/leaveApi`.
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const BASE = '/api/leave';

/** Only ANNUAL draws down the yearly balance; the rest are counted separately. */
export type LeaveType = 'ANNUAL' | 'SICK' | 'UNPAID' | 'SPECIAL_EVENT' | 'SHORT_ABSENCE';

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface LeaveAttachment {
  url: string;
  name: string;
  size?: number;
  contentType?: string;
}

export interface LeaveRequest {
  id: string;
  userId: string;
  user: { id: string; name: string; email: string };
  type: LeaveType;
  status: LeaveStatus;
  /** Inclusive "YYYY-MM-DD" bounds. */
  startDate: string;
  endDate: string;
  /** SHORT_ABSENCE only. */
  hours: number | null;
  /** Working days the range costs; 0 for an hours-long absence. */
  workingDays: number;
  reason: string | null;
  attachments: LeaveAttachment[];
  decidedBy: { id: string; name: string } | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  updatedAt: string;
  /** What the signed-in caller may do with this row, decided server-side. */
  permissions: {
    canEdit: boolean;
    canCancel: boolean;
    canDecide: boolean;
    canDelete: boolean;
  };
}

export interface LeaveBalance {
  userId: string;
  year: number;
  entitledDays: number;
  usedDays: number;
  pendingDays: number;
  remainingDays: number;
  /** The entitlement is the house default, nobody set a number on this person. */
  isDefaultEntitlement: boolean;
  byType: Record<LeaveType, number>;
}

/** One person's approved absence on one calendar day — what the calendar paints. */
export interface LeaveDay {
  requestId: string;
  userId: string;
  userName: string;
  type: LeaveType;
  day: string;
  startDate: string;
  endDate: string;
  hours: number | null;
}

export interface PublicHoliday {
  day: string;
  name: string;
}

export interface LeaveReportRow {
  userId: string;
  personId: string | null;
  name: string;
  email: string;
  balance: LeaveBalance;
}

export interface LeaveFilters {
  userId?: string;
  /** Leave these requests out — the team list passes the viewer's own id. */
  excludeUserId?: string;
  status?: LeaveStatus[];
  type?: LeaveType[];
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export async function listLeaveRequests(
  filters: LeaveFilters = {},
): Promise<{ requests: LeaveRequest[]; total: number }> {
  const res = await apiFetch<Envelope<{ requests: LeaveRequest[]; total: number }>>(
    `${BASE}/requests${query({
      userId: filters.userId,
      excludeUserId: filters.excludeUserId,
      status: filters.status?.join(','),
      type: filters.type?.join(','),
      from: filters.from,
      to: filters.to,
      page: filters.page,
      pageSize: filters.pageSize,
    })}`,
    { headers: authHeaders() },
  );
  // Test Mode's generic fallback has no `requests` key — read defensively.
  return { requests: res.data?.requests ?? [], total: res.data?.total ?? 0 };
}

export interface CreateLeaveInput {
  /** Filing for someone else needs `leave.canManage`. */
  userId?: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  hours?: number | null;
  reason?: string | null;
  attachments?: LeaveAttachment[];
}

export async function createLeaveRequest(input: CreateLeaveInput): Promise<LeaveRequest> {
  const res = await apiFetch<Envelope<{ request: LeaveRequest }>>(`${BASE}/requests`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.request;
}

export type UpdateLeaveInput = Partial<Omit<CreateLeaveInput, 'userId'>>;

export async function updateLeaveRequest(
  id: string,
  patch: UpdateLeaveInput,
): Promise<LeaveRequest> {
  const res = await apiFetch<Envelope<{ request: LeaveRequest }>>(
    `${BASE}/requests/${encodeURIComponent(id)}`,
    { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(patch) },
  );
  return res.data.request;
}

export async function decideLeaveRequest(
  id: string,
  decision: 'approve' | 'reject',
  note?: string | null,
): Promise<LeaveRequest> {
  const res = await apiFetch<Envelope<{ request: LeaveRequest }>>(
    `${BASE}/requests/${encodeURIComponent(id)}/${decision}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ note: note ?? null }) },
  );
  return res.data.request;
}

export async function cancelLeaveRequest(id: string): Promise<LeaveRequest> {
  const res = await apiFetch<Envelope<{ request: LeaveRequest }>>(
    `${BASE}/requests/${encodeURIComponent(id)}/cancel`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data.request;
}

export async function deleteLeaveRequest(id: string): Promise<void> {
  await apiFetch<void>(`${BASE}/requests/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

export async function getLeaveBalance(userId?: string, year?: number): Promise<LeaveBalance> {
  const res = await apiFetch<Envelope<{ balance: LeaveBalance }>>(
    `${BASE}/balance${query({ userId, year })}`,
    { headers: authHeaders() },
  );
  return res.data.balance;
}

export async function listLeaveDays(
  from: string,
  to: string,
  userId?: string,
): Promise<LeaveDay[]> {
  const res = await apiFetch<Envelope<{ days: LeaveDay[] }>>(
    `${BASE}/days${query({ from, to, userId })}`,
    { headers: authHeaders() },
  );
  return res.data?.days ?? [];
}

/**
 * The legal holidays of a year (optionally `through` a later one). The request
 * form counts working days from this list rather than carrying its own copy of
 * the Romanian calendar.
 */
export async function listPublicHolidays(year: number, through?: number): Promise<PublicHoliday[]> {
  const res = await apiFetch<Envelope<{ holidays: PublicHoliday[] }>>(
    `${BASE}/holidays${query({ year, through })}`,
    { headers: authHeaders() },
  );
  return res.data?.holidays ?? [];
}

export async function getPendingLeaveCount(): Promise<number> {
  const res = await apiFetch<Envelope<{ count: number }>>(`${BASE}/pending-count`, {
    headers: authHeaders(),
  });
  return res.data?.count ?? 0;
}

export async function getLeaveReport(
  year: number,
): Promise<{ year: number; rows: LeaveReportRow[] }> {
  const res = await apiFetch<Envelope<{ year: number; rows: LeaveReportRow[] }>>(
    `${BASE}/report${query({ year })}`,
    { headers: authHeaders() },
  );
  return { year: res.data?.year ?? year, rows: res.data?.rows ?? [] };
}

/** The CSV export's URL. The caller opens it; the browser does the download. */
export function leaveReportCsvPath(year: number): string {
  return `${BASE}/report${query({ year, format: 'csv' })}`;
}

export async function setLeaveEntitlement(
  personId: string,
  days: number | null,
): Promise<{ personId: string; annualLeaveDays: number | null }> {
  const res = await apiFetch<Envelope<{ personId: string; annualLeaveDays: number | null }>>(
    `${BASE}/entitlement/${encodeURIComponent(personId)}`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ days }) },
  );
  return res.data;
}
